'use client';

import type { ProfileConfigWidget } from 'src/api/redux/profileConfiguration/types';

import {
  CodeSetVolumeGap,
  CompactCodeSetVolumeByCode,
  CompactRatio,
  CompactEngageStrategy,
  ExpectedOpportunity,
  CompactFreshFacesLocations,
  CompactInboundReferrals,
  CompactOpenPayments,
  CompactOpenPaymentsByCategories,
  CompactOutboundReferrals,
  CompactPayerMix,
  CompactProfessionalMix,
  CompactReimbursement,
  CompactSitesBySpecialty,
  CompactSystemMetrics,
  CompactSystemSites,
  SystemVolume,
  CompactTopProviders,
  CompactTreatingVolume,
  CompactTreatingVolumeV2,
  TreatingVolumeTrends,
  ReferralVolumeTrends,
  CompactVolumeDifference,
  CompactVolumeByLocation,
  PatientVisitValue,
  SlotValue,
} from '..';
import { ProfileAffiliations } from 'src/components/Profiles/components/ProfileAffiliations';
import PlaysSectionV2 from '../PlaysSection/PlaysSectionV2';
import CompactVolumeGap from '../CodeSetVolumeGap/CompactVolumeGap';
import CodeSetVolumeGapV2 from '../CodeSetVolumeGap/CodeSetVolumeGapV2';
import CompactTreatingVolumeRatio from '../CompactTreatingVolumeRatio/CompactTreatingVolumeRatio';
import CompactTreatingVolumeRatioPreview from '../CompactTreatingVolumeRatio/CompactTreatingVolumeRatioPreview';
import CaptureRateWidget from '../CaptureRate/CaptureRate';
import CaptureRatePreview from '../CaptureRate/CaptureRatePreview';

// Preview variant imports (direct paths to avoid circular dependencies)
import CompactTreatingVolumePreview from '../CompactTreatingVolume/CompactTreatingVolumePreview';
import TreatingVolumeTrendsPreview from '../TreatingVolumeTrends/TreatingVolumeTrendsPreview';
import CompactVolumeByLocationPreview from '../CompactVolumeByLocation/CompactVolumeByLocationPreview';
import CompactTopProvidersPreview from '../CompactTopProviders/CompactTopProvidersPreview';
import CompactPayerMixPreview from '../CompactPayerMix/CompactPayerMixPreview';
import CompactReimbursementPreview from '../CompactReimbursement/CompactReimbursementPreview';
import CompactSitesBySpecialtyPreview from '../CompactSitesBySpecialty/CompactSitesBySpecialtyPreview';
import CompactCodeSetVolumeByCodePreview from '../CompactCodeSetVolumeByCode/CompactCodeSetVolumeByCodePreview';
import CompactInboundReferralsPreview from '../CompactReferrals/CompactInboundReferralsPreview';
import CompactOutboundReferralsPreview from '../CompactReferrals/CompactOutboundReferralsPreview';
import CompactOpenPaymentsPreview from '../CompactOpenPayments/CompactOpenPaymentsPreview';
import CompactSystemSitesPreview from '../CompactSystemSites/CompactSystemSitesPreview';
import CompactSystemMetricsPreview from '../CompactSystemMetrics/CompactSystemMetricsPreview';
import SystemVolumePreview from '../SystemVolume/SystemVolumePreview';
import CompactProfessionalMixPreview from '../CompactProfessionalMix/CompactProfessionalMixPreview';
import CompactEngageStrategyPreview from '../CompactEngageStrategy/CompactEngageStrategyPreview';
import ExpectedOpportunityPreview from '../ExpectedOpportunity/ExpectedOpportunityPreview';
import PatientVisitValuePreview from '../PatientVisitValue/PatientVisitValuePreview';
import CompactOpenPaymentsByCategoriesPreview from '../CompactOpenPaymentsByCategories/CompactOpenPaymentsByCategoriesPreview';
import CompactFreshFacesLocationsPreview from '../CompactFreshFacesLocations/CompactFreshFacesLocationsPreview';
import ReferralVolumeTrendsPreview from '../ReferralVolumeTrends/ReferralVolumeTrendsPreview';
import CompactVolumeDifferencePreview from '../CompactVolumeDifference/CompactVolumeDifferencePreview';
import { ProfileAffiliationsPreview } from 'src/components/Profiles/components/ProfileAffiliations';

import type { CodeSetGroup, ProfileSection, WidgetRegistryEntry } from './types';

// Re-export rendering utilities from WidgetRenderer (the cycle-free module)
export {
  WidgetRenderer,
  toProfileWidget,
} from './WidgetRenderer';
import { setWidgetResolver, registerResolverInit } from './WidgetRenderer';

// Lazy singleton to avoid circular-dependency TDZ errors.
// Component bindings are only accessed at render time, not at module evaluation.
let _widgetMap: Record<string, WidgetRegistryEntry> | null = null;

const getWidgetComponentMap = (): Record<string, WidgetRegistryEntry> => {
  if (!_widgetMap) {
    _widgetMap = {
      code_set_treating_volume: { default: CompactTreatingVolume, header: CompactTreatingVolumeV2, preview: CompactTreatingVolumePreview },
      treating_volume_trends: { default: TreatingVolumeTrends, preview: TreatingVolumeTrendsPreview },
      top_locations: { default: CompactVolumeByLocation, preview: CompactVolumeByLocationPreview },
      top_providers_at_site: { default: CompactTopProviders, preview: CompactTopProvidersPreview },
      payer_mix: { default: CompactPayerMix, preview: CompactPayerMixPreview },
      reimbursement: { default: CompactReimbursement, preview: CompactReimbursementPreview },
      sites_by_specialty: { default: CompactSitesBySpecialty, preview: CompactSitesBySpecialtyPreview },
      code_set_volume_by_code: { default: CompactCodeSetVolumeByCode, preview: CompactCodeSetVolumeByCodePreview },
      inbound_referrals: { default: CompactInboundReferrals, preview: CompactInboundReferralsPreview },
      outbound_referrals: { default: CompactOutboundReferrals, preview: CompactOutboundReferralsPreview },
      open_payments: { default: CompactOpenPayments, preview: CompactOpenPaymentsPreview },
      system_sites: { default: CompactSystemSites, preview: CompactSystemSitesPreview },
      system_metrics: { default: CompactSystemMetrics, preview: CompactSystemMetricsPreview },
      system_volume: { default: SystemVolume, preview: SystemVolumePreview },
      healthcare_professional_mix: { default: CompactProfessionalMix, preview: CompactProfessionalMixPreview },
      engage_strategy: { default: CompactEngageStrategy, preview: CompactEngageStrategyPreview },
      code_set_volume_gap: { default: CompactVolumeGap, preview: CodeSetVolumeGapV2 },
      treating_volume_ratio_analysis: { default: CompactTreatingVolumeRatio, preview: CompactTreatingVolumeRatioPreview },
      volume_difference: { default: CompactVolumeDifference, preview: CompactVolumeDifferencePreview },
      patient_visit_value: { default: PatientVisitValue, header: SlotValue, preview: PatientVisitValuePreview },
      affiliations: { default: ProfileAffiliations, preview: ProfileAffiliationsPreview },
      expected_opportunity: { default: ExpectedOpportunity, preview: ExpectedOpportunityPreview },
      open_payments_categories: { default: CompactOpenPaymentsByCategories, preview: CompactOpenPaymentsByCategoriesPreview },
      fresh_faces_locations: { default: CompactFreshFacesLocations, preview: CompactFreshFacesLocationsPreview },
      referral_volume_trends: { default: ReferralVolumeTrends, preview: ReferralVolumeTrendsPreview },
      capture_rate: { default: CaptureRateWidget, preview: CaptureRatePreview },
      plays: { default: PlaysSectionV2 },
    };
    setWidgetResolver((code, variant = 'default') => {
      const entry = _widgetMap?.[code];
      if (!entry) return undefined;
      return entry[variant] ?? entry.default;
    });
  }
  return _widgetMap;
};

export const groupWidgetsByCodeSet = (
  widgets: ProfileConfigWidget[]
): CodeSetGroup[] => {
  const groupMap = new Map<number, CodeSetGroup>();

  for (const widget of widgets) {
    if (!widget.code_set) continue;

    const existing = groupMap.get(widget.code_set.id);
    if (existing) {
      existing.widgets.push(widget);
    } else {
      groupMap.set(widget.code_set.id, {
        codeSet: widget.code_set,
        widgets: [widget],
      });
    }
  }

  return Array.from(groupMap.values());
};

/**
 * Build an ordered list of profile sections from widgets, respecting display_order.
 * Code-set widgets are grouped together and placed at the position of their first widget.
 * Standalone widgets (including plays) are placed individually at their own position.
 */
export const buildOrderedSections = (
  widgets: ProfileConfigWidget[],
  excludeCodes: string[] = []
): ProfileSection[] => {
  const widgetMap = getWidgetComponentMap();
  const filtered = widgets.filter(
    (w) =>
      !excludeCodes.includes(w.code) &&
      (w.code === 'plays' || w.code_set || w.code in widgetMap)
  );

  // Widgets that use code_set for data but should render standalone (not grouped)
  const standaloneCodeSetWidgets = new Set([
    'code_set_volume_gap',
    'code_set_volume_by_code',
    'expected_opportunity',
    'patient_visit_value',
    'sites_by_specialty',
    'open_payments_categories',
    'treating_volume_ratio_analysis',
    'capture_rate',
  ]);

  // Pre-group code set widgets by code_set.id
  const codeSetGroupMap = new Map<number, CodeSetGroup>();
  for (const widget of filtered) {
    if (!widget.code_set || standaloneCodeSetWidgets.has(widget.code)) continue;
    const existing = codeSetGroupMap.get(widget.code_set.id);
    if (existing) {
      existing.widgets.push(widget);
    } else {
      codeSetGroupMap.set(widget.code_set.id, {
        codeSet: widget.code_set,
        widgets: [widget],
      });
    }
  }

  // Walk in API order, placing code set groups at first occurrence
  const sections: ProfileSection[] = [];
  const seenCodeSets = new Set<number>();

  for (const widget of filtered) {
    if (widget.code_set && !standaloneCodeSetWidgets.has(widget.code)) {
      if (!seenCodeSets.has(widget.code_set.id)) {
        seenCodeSets.add(widget.code_set.id);
        const group = codeSetGroupMap.get(widget.code_set.id)!;
        sections.push({ type: 'codeset', group });
      }
    } else if (widget.code === 'plays') {
      sections.push({ type: 'plays', widget });
    } else {
      sections.push({ type: 'standalone', widget });
    }
  }

  return sections;
};

// Register a lazy initializer so WidgetRenderer can trigger getWidgetComponentMap
// on first render. This avoids TDZ errors from eager module-level calls while
// ensuring the resolver works even when buildOrderedSections is never called.
registerResolverInit(() => getWidgetComponentMap());
